import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Application error boundary.
 *
 * Shows a recoverable surface instead of a blank page. Stack traces are only rendered in
 * development builds; production users get a plain message and a reload action.
 */
interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('Unhandled error in React tree', error, info.componentStack);
    }
  }

  private handleReload = (): void => {
    globalThis.location.reload();
  };

  private handleDismiss = (): void => {
    this.setState({ error: null });
    globalThis.location.hash = '#/';
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="ar-screen" data-variant="centred">
        <div className="ar-stack" style={{ textAlign: 'center' }}>
          <h1 className="ar-subtitle">Something went wrong</h1>
          <p className="ar-muted">
            The game hit an unexpected problem. Your saved progress has not been touched.
          </p>
          {import.meta.env.DEV && <pre className="ar-ascii">{error.stack ?? error.message}</pre>}
          <button
            type="button"
            className="ar-button"
            data-variant="primary"
            onClick={this.handleDismiss}
          >
            Back to home
          </button>
          <button type="button" className="ar-button" onClick={this.handleReload}>
            Reload the game
          </button>
        </div>
      </div>
    );
  }
}
